/**
 * MobileTabBar — fixed bottom tab bar + "More" bottom sheet (<768px only).
 * Redesign item 6: Stories · Ask · Reports · Insights · More.
 * Sheet sections: CONTENT (Briefs/Podcast/Newsletter) and MORE (Archive,
 * Settings, About & Subscribe, Admin mode, Dark mode).
 */
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Newspaper, Sparkles, FileText, TrendingUp, Activity, Menu, X, Shield, Sun, Moon } from "lucide-react";
import { useAuth } from "../AuthContext";
import { useDarkMode } from "../hooks/useDarkMode";
import { useAdminMode } from "../hooks/useAdminMode";
import ArchiveModal from "./ArchiveModal";
import SettingsModal from "./SettingsModal";

const TABS = [
  { to: "/feed", key: "stories", label: "Stories", Icon: Newspaper, testid: "mobile-tab-stories" },
  { to: "/pulse", key: "pulse", label: "Pulse", Icon: Activity, testid: "mobile-tab-pulse" },
  { to: "/ask", key: "ask", label: "Ask", Icon: Sparkles, testid: "mobile-tab-ask" },
  { to: "/reports", key: "reports", label: "Reports", Icon: FileText, testid: "mobile-tab-reports" },
  { to: "/insights", key: "insights", label: "Insights", Icon: TrendingUp, testid: "mobile-tab-insights" },
];

export default function MobileTabBar({ active = "", darkMode = false }) {
  const { user, isAdmin, authHeader } = useAuth() || {};
  const [dmPref, setDmPref] = useDarkMode();
  const [adminMode, setAdminMode] = useAdminMode();
  const [sheetOpen, setSheetOpen] = useState(false);
  const navigate = useNavigate();
  const dm = darkMode;

  // Reserve space so the fixed bar never covers content/footer (mobile only)
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const apply = () => { document.body.style.paddingBottom = mq.matches ? "58px" : ""; };
    apply();
    mq.addEventListener("change", apply);
    return () => {
      mq.removeEventListener("change", apply);
      document.body.style.paddingBottom = "";
    };
  }, []);

  const go = (to) => { setSheetOpen(false); navigate(to); };
  const sheetRowCls = `w-full flex items-center gap-3 px-5 py-3 text-left text-sm font-medium transition-colors ${dm ? "text-slate-200 active:bg-white/5" : "text-[#37464c] active:bg-[#f0f5f5]"}`;
  const sectionCls = `px-5 pt-4 pb-1 text-[10px] font-bold uppercase tracking-widest ${dm ? "text-slate-600" : "text-[#96a4a8]"}`;

  return (
    <>
      <nav
        data-testid="mobile-tab-bar"
        className="fixed bottom-0 left-0 right-0 z-40 flex md:hidden"
        style={{
          background: dm ? "rgba(12,16,17,0.97)" : "rgba(255,255,255,0.97)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          borderTop: dm ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e7e8",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {TABS.map(({ to, key, label, Icon, testid }) => {
          const isActive = active === key;
          return (
            <Link
              key={key}
              to={to}
              data-testid={testid}
              className="flex-1 flex flex-col items-center gap-0.5 py-2"
              style={{ color: isActive ? (dm ? "#22d3ee" : "#006874") : (dm ? "#64748b" : "#96a4a8") }}
            >
              <Icon className="w-5 h-5" strokeWidth={isActive ? 2.4 : 1.8} />
              <span className={`text-[9.5px] uppercase tracking-wider ${isActive ? "font-bold" : "font-semibold"}`}>{label}</span>
            </Link>
          );
        })}
        <button
          data-testid="mobile-tab-more"
          onClick={() => setSheetOpen(true)}
          className="flex-1 flex flex-col items-center gap-0.5 py-2"
          style={{ color: sheetOpen ? (dm ? "#22d3ee" : "#006874") : (dm ? "#64748b" : "#96a4a8") }}
        >
          <Menu className="w-5 h-5" strokeWidth={1.8} />
          <span className="text-[9.5px] uppercase tracking-wider font-semibold">More</span>
        </button>
      </nav>

      {sheetOpen && (
        <>
          <div
            data-testid="mobile-more-overlay"
            className="fixed inset-0 z-40 md:hidden"
            style={{ background: "rgba(0,0,0,0.5)" }}
            onClick={() => setSheetOpen(false)}
          />
          <div
            data-testid="mobile-more-sheet"
            className={`fixed bottom-0 left-0 right-0 z-[45] md:hidden rounded-t-2xl overflow-y-auto animate-fade-in ${dm ? "bg-[#141a1b]" : "bg-white"}`}
            style={{ maxHeight: "78vh", paddingBottom: "calc(env(safe-area-inset-bottom) + 10px)", boxShadow: "0 -12px 40px rgba(0,0,0,0.25)" }}
          >
            <div className="flex items-center justify-between px-5 pt-3 pb-1">
              <span className={`w-9 h-1 rounded-full ${dm ? "bg-white/15" : "bg-black/10"}`} />
              <button data-testid="mobile-more-close" onClick={() => setSheetOpen(false)} className={dm ? "text-slate-500" : "text-[#96a4a8]"}>
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className={sectionCls}>Content</p>
            <button data-testid="sheet-briefs-link" onClick={() => go("/briefs")} className={sheetRowCls}>
              Briefs<span className={`ml-auto text-[11px] font-normal ${dm ? "text-slate-500" : "text-[#96a4a8]"}`}>Weekly editorial brief</span>
            </button>
            <button data-testid="sheet-podcast-link" onClick={() => go("/podcast")} className={sheetRowCls}>
              Podcast<span className={`ml-auto text-[11px] font-normal ${dm ? "text-slate-500" : "text-[#96a4a8]"}`}>Audio briefing</span>
            </button>
            <button data-testid="sheet-newsletters-link" onClick={() => go("/newsletters")} className={sheetRowCls}>
              Newsletter<span className={`ml-auto text-[11px] font-normal ${dm ? "text-slate-500" : "text-[#96a4a8]"}`}>Subscribe &amp; issues</span>
            </button>
            <button data-testid="sheet-events-link" onClick={() => go("/events")} className={sheetRowCls}>
              Events<span className={`ml-auto text-[11px] font-normal ${dm ? "text-slate-500" : "text-[#96a4a8]"}`}>Industry calendar</span>
            </button>

            <p className={sectionCls}>More</p>
            {user && (
              <div className={`${sheetRowCls} justify-start [&>button]:text-inherit`} data-testid="sheet-archive-row">
                <ArchiveModal darkMode={dm} authHeader={authHeader} triggerTestId="archive-modal-trigger-mobile" />
              </div>
            )}
            {user && (
              <div className={`${sheetRowCls} justify-start [&>button]:text-inherit`} data-testid="sheet-settings-row">
                <SettingsModal darkMode={dm} authHeader={authHeader} triggerTestId="settings-modal-trigger-mobile" />
              </div>
            )}
            <button data-testid="sheet-about-link" onClick={() => go("/about")} className={sheetRowCls}>About</button>
            <button data-testid="sheet-subscribe-link" onClick={() => go("/subscribe")} className={sheetRowCls}>Subscribe</button>
            {isAdmin && (
              <button
                data-testid="sheet-admin-mode-toggle"
                onClick={() => setAdminMode((v) => !v)}
                className={`${sheetRowCls} ${adminMode ? (dm ? "!text-amber-300" : "!text-[#8a5a06]") : ""}`}
              >
                <Shield className="w-4 h-4" />Admin mode
                <span
                  className={`ml-auto text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${adminMode ? "text-white" : (dm ? "bg-[#1a2122] text-slate-500" : "bg-[#eef1f2] text-[#5b6b70]")}`}
                  style={adminMode ? { background: "#8a5a06" } : undefined}
                >
                  {adminMode ? "On" : "Off"}
                </span>
              </button>
            )}
            <button data-testid="sheet-dark-toggle" onClick={() => setDmPref((v) => !v)} className={sheetRowCls}>
              {dmPref ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              {dmPref ? "Light theme" : "Dark theme"}
            </button>
          </div>
        </>
      )}
    </>
  );
}
